import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Divider, Drawer, Input, message, Select, Table, Tooltip } from 'antd';
import {
  AlignLeftOutlined,
  BoldOutlined,
  CloseOutlined,
  FontColorsOutlined,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  SearchOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { config } from './definitions/vendor-share/config';
import { api } from './definitions/vendor-share/api';
import CustomPagination from '../CustomPagination';
import AppTypography from '../typography/Typography';

const TOOLBAR_BUTTONS = [
  { icon: <BoldOutlined />, title: 'Bold' },
  { icon: <ItalicOutlined />, title: 'Italic' },
  { icon: <UnderlineOutlined />, title: 'Underline' },
  { icon: <LinkOutlined />, title: 'Link' },
  { icon: <OrderedListOutlined />, title: 'Ordered list' },
  { icon: <UnorderedListOutlined />, title: 'Bullet list' },
  { icon: <FontColorsOutlined />, title: 'Font color' },
  { icon: <HighlightOutlined />, title: 'Highlight' },
  { icon: <AlignLeftOutlined />, title: 'Align' },
  { icon: <PaperClipOutlined />, title: 'Attachment' },
];

const TEXT_STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
];

function parseEmails(value) {
  return value.split(',').map((e) => e.trim()).filter(Boolean);
}

const vendorColumns = config.vendorColumns.map((col) => ({
  ...col,
  render: (val) => val || '-',
}));

export default function VendorShareDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedIds = [],
    selectedRecords = [],
    recipients = [],
    subject: initialSubject = '',
    onSend,
  } = location.state ?? {};

  const jobId = selectedIds[0] ?? selectedRecords[0]?._id ?? selectedRecords[0]?.id;

  const [to, setTo] = useState(recipients.join(', '));
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const editorRef = useRef(null);

  const [vendorTab, setVendorTab] = useState(config.defaultTab);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [myVendorCount, setMyVendorCount] = useState(0);
  const [allVendorCount, setAllVendorCount] = useState(0);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorPageSize, setVendorPageSize] = useState(config.defaultPageSize);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendorKeys, setSelectedVendorKeys] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setVendorLoading(true);
    const limit = vendorSearch ? 100 : vendorPageSize;
    const offset = vendorSearch ? 0 : (vendorPage - 1) * vendorPageSize;
    api.getVendorList({ jobId, limit, offset, type: vendorTab })
      .then((res) => {
        if (cancelled) return;
        const vendorList = res?.data?.vendorList ?? res?.data ?? [];
        const myCount = res?.data?.myVendorCount ?? 0;
        const allCount = res?.data?.allVendorCount ?? 0;
        setVendors(Array.isArray(vendorList) ? vendorList : []);
        setMyVendorCount(myCount);
        setAllVendorCount(allCount);
        setVendorTotal(vendorTab === 'myVendor' ? myCount : allCount);
      })
      .catch(() => { if (!cancelled) { setVendors([]); setVendorTotal(0); } })
      .finally(() => { if (!cancelled) setVendorLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, vendorTab, vendorPage, vendorPageSize, vendorSearch]);

  useEffect(() => {
    if (!jobId) return;
    api.getMailDetails(jobId).then((data) => {
      if (data.subject) setSubject(data.subject);
      if (data.contents && editorRef.current) editorRef.current.innerHTML = data.contents;
    }).catch(() => {});
  }, [jobId]);

  useEffect(() => {
    const emails = selectedVendorKeys
      .map((key) => vendors.find((v) => v.vendorId === key)?.vendorEmail ?? '')
      .filter(Boolean);
    if (emails.length > 0) {
      setBcc(emails.join(', '));
      setShowBcc(true);
    } else {
      setBcc('');
    }
  }, [selectedVendorKeys, vendors]);

  const canSend = (to.trim() || bcc.trim()) && subject.trim();
  const tabCount = (key) => key === 'myVendor' ? myVendorCount : allVendorCount;

  function handleClose() { navigate(-1); }

  function handleSend() {
    if (!canSend) return;
    const contents = editorRef.current?.innerHTML ?? '';
    api.submit({
      jobId,
      toMail: parseEmails(to),
      ccMail: parseEmails(cc),
      bccMail: parseEmails(bcc),
      subject,
      contents,
    }).then((res) => {
      if (res?.status === true) {
        message.success(res?.data || 'Mail sent successfully.');
        if (typeof onSend === 'function') onSend({ to, cc, bcc, subject, message: contents, selectedIds });
        navigate(-1);
      } else {
        message.error(res?.message || res?.data || 'Failed to send mail. Please try again.');
      }
    }).catch((err) => { message.error(err?.message || 'Failed to send mail. Please try again.'); });
  }

  const filteredVendors = useMemo(() => {
    if (!vendorSearch) return vendors;
    const q = vendorSearch.toLowerCase();
    return vendors.filter((v) =>
      (v.vendorName ?? '').toLowerCase().includes(q)
      || (v.vendorEmail ?? '').toLowerCase().includes(q)
      || (v.vendorLocation ?? '').toLowerCase().includes(q),
    );
  }, [vendors, vendorSearch]);

  const footer = (
    <div className="vendor-share__footer">
      <Button onClick={handleClose}>Cancel</Button>
      <Button type="primary" disabled={!canSend} onClick={handleSend}>Send</Button>
    </div>
  );

  return (
    <Drawer
      open
      placement="right"
      onClose={handleClose}
      closable={false}
      rootClassName="vendor-share"
      width="95%"
      footer={footer}
    >
      <div className="vendor-share__body">

        {/* ── Left: Vendor List ── */}
        <div className="vendor-share__left">
          {/* vendor-header: close + title + search (flex row), tabs below */}
          <div className="vendor-share__vendor-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                size="small"
                style={{ marginRight: 8 }}
              />
              <AppTypography tag="span" variant="h2">Vendor List</AppTypography>
              {/* margin-left:auto in CSS pushes search to the right */}
              <div className="vendor-share__vendor-search">
                <SearchOutlined className="vendor-share__search-icon" />
                <Input
                  variant="borderless"
                  placeholder="Search vendor"
                  value={vendorSearch}
                  onChange={(e) => { setVendorSearch(e.target.value); setVendorPage(1); }}
                  className="vendor-share__search-input"
                />
              </div>
            </div>

            <div className="vendor-share__vendor-tabs">
              {config.vendorTabs.map((tab) => (
                <span
                  key={tab.key}
                  className={`vendor-share__vendor-tab${vendorTab === tab.key ? ' vendor-share__vendor-tab--active' : ''}`}
                  onClick={() => { setVendorTab(tab.key); setVendorPage(1); setSelectedVendorKeys([]); }}
                >
                  {tab.label} ({tabCount(tab.key)})
                </span>
              ))}
            </div>
          </div>

          <div className="vendor-share__vendor-table-wrapper">
            <Table
              rowKey={(record) => record.vendorId}
              columns={vendorColumns}
              dataSource={filteredVendors}
              loading={vendorLoading}
              pagination={false}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedVendorKeys,
                onChange: (keys) => {
                  if (keys.length > config.maxVendorSelect) return;
                  setSelectedVendorKeys(keys);
                },
              }}
              scroll={{ y: 'calc(100vh - 340px)' }}
            />
          </div>

          <div className="vendor-share__vendor-footer">
            <CustomPagination
              current={vendorPage}
              pageSize={vendorPageSize}
              total={vendorTotal}
              onChange={(page) => setVendorPage(page)}
              onPageSizeChange={(size) => setVendorPageSize(size)}
            />
          </div>
        </div>

        {/* ── Right: Share / Compose ── */}
        <div className="vendor-share__right">
          <div className="mail-popup__body">

            {/* Share heading + hint */}
            <div style={{ padding: '16px 20px 8px' }}>
              <AppTypography tag="h2" variant="h2" color="link">Share</AppTypography>
              <AppTypography variant="caption" color="muted">
                Add email ID separated by commas
              </AppTypography>
            </div>

            <Divider style={{ margin: 0 }} />

            {/* To */}
            <div className="mail-popup__field-row">
              <AppTypography variant="body" color="muted" className="mail-popup__field-label">To</AppTypography>
              <Input
                variant="borderless"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mail-popup__field-input"
              />
              <div className="mail-popup__cc-bcc-actions">
                {!showCc && (
                  <span className="mail-popup__cc-bcc-link" onClick={() => setShowCc(true)}>Cc</span>
                )}
                {!showBcc && (
                  <span className="mail-popup__cc-bcc-link" onClick={() => setShowBcc(true)}>Bcc</span>
                )}
              </div>
            </div>
            <Divider style={{ margin: 0 }} />

            {showCc && (
              <>
                <div className="mail-popup__field-row">
                  <AppTypography variant="body" color="muted" className="mail-popup__field-label">Cc</AppTypography>
                  <Input
                    variant="borderless"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    className="mail-popup__field-input"
                  />
                </div>
                <Divider style={{ margin: 0 }} />
              </>
            )}

            {showBcc && (
              <>
                <div className="mail-popup__field-row">
                  <AppTypography variant="body" color="muted" className="mail-popup__field-label">Bcc</AppTypography>
                  <Input
                    variant="borderless"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                    placeholder="Vendor emails (auto-populated)"
                    className="mail-popup__field-input"
                  />
                </div>
                {selectedVendorKeys.length > 0 && (
                  <div className="vendor-share__selected-count">
                    {selectedVendorKeys.length} / {config.maxVendorSelect} Vendors selected
                  </div>
                )}
                <Divider style={{ margin: 0 }} />
              </>
            )}

            {/* Subject */}
            <div className="mail-popup__field-row">
              <AppTypography variant="body" color="muted" className="mail-popup__field-label">
                Subject <span style={{ color: 'var(--color-danger, #e74c3c)' }}>*</span>
              </AppTypography>
              <Input
                variant="borderless"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject"
                className="mail-popup__field-input"
              />
            </div>
            <Divider style={{ margin: 0 }} />

            {/* Message */}
            <div className="mail-popup__message-section">
              <AppTypography variant="label" color="muted" className="mail-popup__message-label">
                Message
              </AppTypography>
              <div className="mail-popup__editor-box">
                <div className="mail-popup__toolbar">
                  <Select
                    size="small"
                    defaultValue="normal"
                    variant="borderless"
                    className="mail-popup__toolbar-style-select"
                    options={TEXT_STYLE_OPTIONS}
                    popupMatchSelectWidth={false}
                  />
                  {TOOLBAR_BUTTONS.map(({ icon, title }) => (
                    <Tooltip key={title} title={title} mouseEnterDelay={0.5}>
                      <Button type="text" size="small" icon={icon} className="mail-popup__toolbar-btn" />
                    </Tooltip>
                  ))}
                </div>
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="mail-popup__textarea"
                />
              </div>
            </div>

          </div>
        </div>

      </div>
    </Drawer>
  );
}
